// Helper functions for checking feature availability in plans
import store from "@/store";
import { PRODUCTS } from "./product";
import { PLAN_FEATURE_MAP } from "./const";

export const getFeatFromCurrentPlan = ({ featureType, orderType, currentPlanDetails }) => {
  const _currentPlanDetails = currentPlanDetails || store.getters.currentPlanDetails;
  let productDetails;

  if (orderType === PRODUCTS.MAIL_SUITE) {
    productDetails = _currentPlanDetails?.mailSuiteDetails;
  } else if (orderType === PRODUCTS.SITE) {
    productDetails = _currentPlanDetails?.siteDetails;
  }

  if (!productDetails || !productDetails.planDetails) return false;

  const features = productDetails.planDetails.features || [];

  return features.find(feature => feature.type === featureType);
};

/**
 * Checks if a specific feature is available in the current plan
 * @param {string} featureType - The type of feature to check for, from PLAN_FEATURE_MAP
 * @returns {boolean} - True if the feature is available, false otherwise
 */
export const isFeatureEnabledInCurrentPlan = (featureType, orderType, currentPlanDetails) => {
  const matchingFeat = getFeatFromCurrentPlan({
    featureType,
    orderType,
    currentPlanDetails,
  });
  return matchingFeat && !matchingFeat?.attrs?.hide;
};

/**
 * Checks if a specific feature is active (present and not hidden) in a given plan
 * @param {string} featureType - The type of feature to check for
 * @param {Object} plan - The plan object to check
 * @param {Array} plan.features - Array of features in the plan
 * @param {Object} plan.features[].attrs - Feature attributes including visibility
 * @returns {boolean} - True if feature is present and not hidden, false otherwise
 */
export const isFeatureActiveInPlan = (featureType, plan) => {
  if (!plan || !plan.features || !plan.features.length) return false;

  return plan.features.some(feature => feature.type === featureType && !feature.attrs?.hide);
};

/**
 * Checks if a specific feature is active in any of the provided plans
 * @param {string} featureType - The type of feature to check for
 * @param {Array<Object>} plans - Array of plan objects to check
 * @returns {boolean} - True if feature is active in at least one plan, false otherwise
 */
export const isFeaturePresentInAnyPlan = (featureType, plans) => {
  if (!plans || !plans.length) return false;

  return plans.some(plan => isFeatureActiveInPlan(featureType, plan));
};

export const isEmailBackupDisabledInCurrentPlan = currentPlanDetails => {
  const matchingFeat = getFeatFromCurrentPlan({
    featureType: PLAN_FEATURE_MAP.EMAIL_BACKUP,
    orderType: PRODUCTS.MAIL_SUITE,
    currentPlanDetails,
  });
  return matchingFeat && matchingFeat?.attrs?.hide?.toString() === "true";
};

/**
 * Checks if email backup feature is present in the current plan
 * @param {Object} currentPlanDetails - Details of the current plan including mailSuite and site details
 * @returns {boolean} - True if email backup is enabled in current plan, false otherwise
 */
export const isEmailBackupPresentInCurrentPlan = currentPlanDetails => {
  return isFeatureEnabledInCurrentPlan(
    PLAN_FEATURE_MAP.EMAIL_BACKUP,
    PRODUCTS.MAIL_SUITE,
    currentPlanDetails
  );
};

/**
 * Checks if email backup feature is active in any of the provided plans
 * @param {Array<Object>} plans - Array of plan objects to check
 * @returns {boolean} - True if email backup is active in at least one plan, false otherwise
 */
export const isEmailBackupPresentInAnyPlan = plans => {
  return isFeaturePresentInAnyPlan(PLAN_FEATURE_MAP.EMAIL_BACKUP, plans);
};

/**
 * check if bundle is eligible to show email backup screen
 * @param {*} currentPlanDetails - should not have email backup with hide
 * @param {*} plans - at least one plan should have email backup and it should not be hidden
 * @returns boolean
 */
export const isEmailBackupEligible = (currentPlanDetails, plans) => {
  return (
    !isEmailBackupDisabledInCurrentPlan(currentPlanDetails) && isEmailBackupPresentInAnyPlan(plans)
  );
};
